import React from 'react';
import { Link } from 'react-router-dom';
import './LegalPage.css';

const TermsOfService: React.FC = () => {
  return (
    <div className="legal-page">
      <header className="legal-page__header">
        <div className="legal-page__container">
          <Link to="/" className="legal-page__logo">
            <img src="/ralli-logo.svg" alt="RalliPB" className="legal-page__logo-img" />
            <span>RalliPB</span>
          </Link>
        </div>
      </header>

      <main className="legal-page__content">
        <div className="legal-page__container">
          <h1>Terms of Service</h1>
          <p className="legal-page__updated">Last updated: June 26, 2026</p>

          <section>
            <h2>1. Agreement to Terms</h2>
            <p>
              By accessing or using RalliPB's mobile application and website (the "Platform"), you agree
              to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms,
              please do not use our Platform.
            </p>
          </section>

          <section>
            <h2>2. Description of Service</h2>
            <p>
              RalliPB is a pickleball community platform that helps users discover courts, check in at
              courts, see friend activity, create and respond to scheduled sessions, communicate with
              friends and groups, and manage a player profile.
            </p>
          </section>

          <section>
            <h2>3. User Accounts</h2>

            <h3>3.1 Account Creation</h3>
            <p>
              To use certain features of RalliPB, you must create an account. You agree to provide accurate,
              current, and complete information during registration and to keep your account information updated.
            </p>

            <h3>3.2 Account Security</h3>
            <p>
              You are responsible for maintaining the confidentiality of your account credentials and for
              all activities that occur under your account. You agree to notify us immediately of any
              unauthorized use of your account.
            </p>

            <h3>3.3 Age Requirement</h3>
            <p>
              You must be at least 13 years old to create an account and use RalliPB. By creating an account,
              you represent that you meet this age requirement.
            </p>
          </section>

          <section>
            <h2>4. Skill Ratings and Profile Information</h2>

            <h3>4.1 Self-Rating</h3>
            <p>
              RalliPB lets you set and display a self-assessed pickleball skill level. You are responsible
              for keeping this information accurate and appropriate.
            </p>

            <h3>4.2 Optional Rating Information</h3>
            <p>
              RalliPB may display rating information when that information is available
              for your account. Third-party rating services are independent from RalliPB, and we do not
              guarantee the accuracy, availability, or timing of third-party rating data.
            </p>
          </section>

          <section>
            <h2>5. User Conduct</h2>
            <p>You agree not to:</p>
            <ul>
              <li>Use the Platform for any unlawful purpose or in violation of these Terms</li>
              <li>Impersonate any person or entity, including other RalliPB users</li>
              <li>Harass, abuse, or harm other users</li>
              <li>Post false, misleading, or inappropriate content</li>
              <li>Attempt to gain unauthorized access to the Platform or other users' accounts</li>
              <li>Interfere with or disrupt the Platform's functionality</li>
              <li>Use automated systems to access the Platform without permission</li>
              <li>Circumvent any security measures or access restrictions</li>
            </ul>
          </section>

          <section>
            <h2>6. Content and Intellectual Property</h2>

            <h3>6.1 Your Content</h3>
            <p>
              You retain ownership of content you post on RalliPB, such as profile information, messages,
              check-in notes, and feedback. By posting content, you grant us a non-exclusive, worldwide,
              royalty-free license to use, display, and distribute your content in connection with operating
              the Platform.
            </p>

            <h3>6.2 Our Content</h3>
            <p>
              The Platform, including its design, features, and content excluding user-generated content,
              is owned by RalliPB and protected by intellectual property laws. You may not copy, modify,
              distribute, or create derivative works from our content without permission.
            </p>
          </section>

          <section>
            <h2>7. Location Services</h2>
            <p>
              RalliPB uses location data, with your permission, to show nearby courts, sort courts by distance,
              support check-in features, and help with optional location-based experiences. You may disable
              location services at any time, but this may limit certain features.
            </p>
          </section>

          <section>
            <h2>8. Push Notifications</h2>
            <p>
              RalliPB may send push notifications about court activity, join intents, friend requests, session
              invitations, session responses, messages, and other app updates. You can manage notification
              permissions in your device settings.
            </p>
          </section>

          <section>
            <h2>9. Disclaimers</h2>
            <p>
              THE PLATFORM IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND,
              EITHER EXPRESS OR IMPLIED. WE DO NOT GUARANTEE THAT THE PLATFORM WILL BE UNINTERRUPTED,
              SECURE, OR ERROR-FREE.
            </p>
            <p>
              We do not verify the accuracy of user-provided information, including skill levels, check-ins,
              court conditions, or court availability. You use information from other users at your own risk.
            </p>
          </section>

          <section>
            <h2>10. Limitation of Liability</h2>
            <p>
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, RALLI SHALL NOT BE LIABLE FOR ANY INDIRECT,
              INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS OR
              REVENUES, WHETHER INCURRED DIRECTLY OR INDIRECTLY.
            </p>
          </section>

          <section>
            <h2>11. Indemnification</h2>
            <p>
              You agree to indemnify and hold harmless RalliPB and its officers, directors, employees,
              and agents from any claims, damages, losses, or expenses arising from your use of the
              Platform or violation of these Terms.
            </p>
          </section>

          <section>
            <h2>12. Termination</h2>
            <p>
              We may suspend or terminate your account at any time for violations of these Terms or
              for any other reason at our discretion. You may request account deletion through the app
              or by contacting support.
            </p>
          </section>

          <section>
            <h2>13. Changes to Terms</h2>
            <p>
              We may modify these Terms at any time. We will notify you of material changes by posting
              the updated Terms on the Platform and updating the "Last updated" date. Your continued use
              of the Platform after changes constitutes acceptance of the modified Terms.
            </p>
          </section>

          <section>
            <h2>14. Governing Law</h2>
            <p>
              These Terms shall be governed by and construed in accordance with the laws of the United States,
              without regard to conflict of law principles.
            </p>
          </section>

          <section>
            <h2>15. Dispute Resolution</h2>
            <p>
              Any disputes arising from these Terms or your use of the Platform shall first be attempted
              to be resolved through good-faith negotiation. If negotiation fails, disputes shall be
              resolved through binding arbitration in accordance with the rules of the American Arbitration
              Association.
            </p>
          </section>

          <section>
            <h2>16. Contact Information</h2>
            <p>
              If you have questions about these Terms of Service, please contact us at:
            </p>
            <p>
              <strong>Email:</strong> <a href="mailto:legal@ralli.app">legal@ralli.app</a><br />
              <strong>Support:</strong> <Link to="/support">RalliPB Support</Link><br />
              <strong>Address:</strong> RalliPB, Inc.
            </p>
          </section>
        </div>
      </main>

      <footer className="legal-page__footer">
        <div className="legal-page__container">
          <p>&copy; 2026 RalliPB. All rights reserved.</p>
          <div className="legal-page__footer-links">
            <Link to="/privacy">Privacy Policy</Link>
            <Link to="/terms">Terms of Service</Link>
            <Link to="/support">Support</Link>
            <Link to="/">Home</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default TermsOfService;
